"use client";
import { Icons, Images } from "@assets/assets";
import React, { useEffect, useState } from "react";
import IndustryHeader from "./IndustryHeader";
import Image, { type StaticImageData } from "next/image";

interface WhychooseIndustryTabItem {
  title: string;
  content: string;
  iconSrc?: string;
}

interface WhychooseIndustryProps {
  header: {
    title: React.ReactNode | string;
    subtitle: string;
    description: string;
    buttonText?: string;
    buttonIcon?: string;
    mode: any;
  };
  tabs: WhychooseIndustryTabItem[];
  initialActive?: number;
  watermarkIconSrc?: string | StaticImageData;
  tabChevronIconSrc?: string | StaticImageData;
  highlightColor?: string; // color for the last word in the right card title
  sectionId?: string;
}

export default function WhychooseIndustry({
  header,
  tabs,
  initialActive = 0,
  watermarkIconSrc = Icons?.security as unknown as string | StaticImageData,
  tabChevronIconSrc = Icons?.chevronWhiteLeft as unknown as
    | string
    | StaticImageData,
  highlightColor = "#225BD4",
  sectionId = "why-choose-industry-2",
}: WhychooseIndustryProps) {
  const [active, setActive] = useState(
    Math.min(Math.max(initialActive, 0), Math.max(tabs.length - 1, 0)),
  );
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth < 1024);
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => {
      window.removeEventListener("resize", updateIsMobile);
    };
  }, []);

  const splitTitle = (title: string) => {
    const parts = title.trim().split(" ");
    if (parts.length === 1) return { first: title, last: "" };
    const last = parts.pop() as string;
    return { first: parts.join(" "), last };
  };

  const activeTitle = tabs[active]?.title ?? "";
  const activeContent = tabs[active]?.content ?? "";
  const activeIcon = tabs[active]?.iconSrc ?? watermarkIconSrc;
  const activeParts = splitTitle(activeTitle);

  return (
    <section
      id={sectionId}
      className="why-choose-industry overflow-hidden bg-[#000000] relative pb-[20px] sm:pb-[150px] w-full "
    >
      <div className="texture-bg mix-blend-overlay  w-full h-full absolute top-0 left-0 z-0">
        {" "}
        <div className="absolute inset-0 z-0">
          <Image
            src={Images?.texture}
            alt="texture"
            fill
            className="object-cover mix-blend-overlay opacity-60"
            sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
          />
        </div>
      </div>
      <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px] absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3" />
      <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px]  absolute left-0 bottom-0  -translate-x-1/3 translate-y-1/3" />

      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] py-[40px] lg:pt-[150px] relative z-1">
        <IndustryHeader
          title={header.title}
          subtitle={header.subtitle}
          description={header.description}
          buttonText={header.buttonText}
          buttonIcon={header.buttonIcon}
          mode={header.mode ?? "dark"}
        />

        {/* Tabs + Content */}
        {isMobile ? (
          <div className="mt-[30px] lg:mt-[60px] grid grid-cols-1 gap-[20px]">
            {tabs?.map((tab) => {
              const parts = splitTitle(tab.title);
              return (
                <div
                  key={tab.title}
                  className="relative rounded-[20px] bg-white p-[30px] md:p-[40px] flex overflow-hidden gap-[20px]"
                >
                  <Image
                    src={
                      (tab.iconSrc ?? watermarkIconSrc) as
                        | string
                        | StaticImageData
                    }
                    alt=""
                    width={120}
                    height={120}
                    className="hidden md:block select-none pointer-events-none"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                  <div className="relative z-10 w-full grid grid-cols-1 gap-[12px]">
                    <h3 className="font-medium clash-font text-[length:var(--tab-content-title)] leading-[1.1] text-[#00040b]">
                      {parts.first}
                      {parts.last && (
                        <span
                          className="block"
                          style={{ color: highlightColor }}
                        >
                          {parts.last}
                        </span>
                      )}
                    </h3>
                    <p
                      className="text-[length:var(--primarybtn)] leading-[1.6] text-[#636364]"
                      dangerouslySetInnerHTML={{ __html: tab.content ?? "" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-[30px] lg:mt-[60px] grid grid-cols-1 lg:grid-cols-12 gap-[45px]">
            {/* Left list */}
            <div className="lg:col-span-4 bg-transparent h-full">
              <div className="flex flex-col">
                {tabs?.map((tab, idx) => (
                  <div key={tab.title}>
                    <button
                      type="button"
                      onClick={() => setActive(idx)}
                      aria-label={`Switch to ${tab.title} tab`}
                      className={`w-full flex items-center cursor-pointer justify-between py-[20px] transition-colors ${
                        active === idx ? "opacity-100" : "opacity-40"
                      }`}
                    >
                      <span
                        className={`text-[length:var(--why-choose-tab-link)] clash-font text-left ${
                          active === idx
                            ? "text-white font-medium"
                            : "text-white font-normal"
                        }`}
                      >
                        {tab.title}
                      </span>
                      <Image
                        src={tabChevronIconSrc as string | StaticImageData}
                        alt=""
                        width={12}
                        height={12}
                        className="w-3 h-3 shrink-0 opacity-60"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </button>
                    {/* Divider */}
                    {idx !== tabs.length - 1 && (
                      <div className="h-px w-full bg-[#636364]/50" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right card */}
            <div className="lg:col-span-8 h-full">
              <div
                className="relative h-full bg-white p-[30px]
                         md:p-[40px] lg:p-[50px] flex overflow-hidden gap-[40px]"
              >
                {/* Watermark icon */}
                <div className="hidden xl:flex flex-1 items-center justify-center p-[40px]">
                  <Image
                    src={activeIcon as string | StaticImageData}
                    alt=""
                    width={156}
                    height={156}
                    className="select-none pointer-events-none w-full max-w-[290px] h-auto object-contain "
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </div>
                <div className="relative flex-[3] z-10 place-content-center w-full grid grid-cols-1 gap-[20px]">
                  <h3 className="font-medium clash-font text-[length:var(--tab-content-title)] leading-[1.1] text-[#00040b]">
                    {activeParts.first}
                    {activeParts.last && (
                      <span className="block" style={{ color: highlightColor }}>
                        {activeParts.last}
                      </span>
                    )}
                  </h3>
                  <p
                    className="text-[length:var(--primarybtn)] leading-[1.6] text-[#636364] max-w-[680px]"
                    dangerouslySetInnerHTML={{
                      __html: tabs[active].content ?? "",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
